import React from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { Product, BillingCycle, ProductFeature } from '../services/api';

interface ProductPreviewProps {
  product: Product;
  currentPrice?: number;
}

const ProductPreview: React.FC<ProductPreviewProps> = ({ product, currentPrice }) => {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatFeatureValue = (value: string, unit?: string | null): string => {
    const valueLower = value.toLowerCase();

    // Handle boolean values (for checkbox-only features)
    if (valueLower === 'true') {
      return 'enabled'; // Will show checkmark
    }
    if (valueLower === 'false') {
      return 'disabled';
    }

    // Handle unlimited values
    if (valueLower === 'unlimited') {
      return 'unlimited';
    }

    // Handle numeric values with unit
    const num = parseFloat(value);
    if (!isNaN(num)) {
      if (unit) {
        // If unit exists, format with unit
        if (unit.includes('/bln') || unit.includes('x/bln')) {
          return `${num}${unit}`;
        }
        return `${num} ${unit}`;
      }
      // No unit, return number as string
      return value;
    }

    // Handle special string values
    if (value === 'ALL_PLATFORM') {
      return 'All Platform';
    } else if (value === 'THREE_PLATFORM') {
      return '3 Platform';
    } else if (value === 'ONE_PLATFORM') {
      return '1 Platform';
    }

    // Return as-is for other values
    return value;
  };

  const shouldHighlight = (value: string): boolean => {
    const valueLower = value.toLowerCase();

    // Highlight enabled/unlimited features
    if (valueLower === 'unlimited' || valueLower === 'true') {
      return true;
    }

    // Highlight positive numeric values
    const num = parseFloat(value);
    if (!isNaN(num) && num > 0) {
      return true;
    }

    // Highlight special platform values
    if (value === 'ALL_PLATFORM' || value === 'THREE_PLATFORM') {
      return true;
    }

    return false;
  };

  const getFeatureDisplayName = (feature: ProductFeature): string => {
    // Use feature name from database, fallback to code if name not available
    return feature.name || feature.code;
  };

  const displayPrice = currentPrice !== undefined ? currentPrice : product.basePrice;
  const billingLabel = product.billingCycle === BillingCycle.MONTHLY ? 'bln' : 'thn';

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 px-4 py-3">
        <h3 className="text-lg font-bold text-white">{product.name}</h3>
        <p className="text-sm text-green-100 mt-1">
          {formatPrice(displayPrice)} / {billingLabel}
        </p>
      </div>

      {/* Features List */}
      <div className="divide-y divide-gray-100">
        {product.featureLimits && product.featureLimits.length > 0 ? (
          product.featureLimits.map((limit, index) => {
            const feature = limit.feature;
            if (!feature) return null;

            const displayValue = formatFeatureValue(limit.value, feature.unit);
            const isHighlighted = shouldHighlight(limit.value);
            const valueLower = limit.value.toLowerCase();
            const isCheckmark = displayValue === 'enabled';
            const isDisabled = displayValue === 'disabled';
            const isUnlimited = valueLower === 'unlimited' || valueLower === 'true';

            return (
              <div
                key={limit.id || index}
                className={`px-4 py-3 flex items-center justify-between ${isHighlighted ? 'bg-green-50' : 'bg-white'
                  }`}
              >
                <span className="text-sm font-medium text-gray-900">
                  {getFeatureDisplayName(feature)}
                </span>
                <div className="flex items-center">
                  {isCheckmark ? (
                    <CheckIcon className="w-5 h-5 text-green-600" />
                  ) : isDisabled ? (
                    <span className="text-gray-400">—</span>
                  ) : isUnlimited ? (
                    <span className="text-sm font-semibold text-green-700">Unlimited</span>
                  ) : (
                    <span className={`text-sm font-medium ${isHighlighted ? 'text-green-700' : 'text-gray-700'
                      }`}>
                      {displayValue}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="px-4 py-8 text-center text-gray-500 text-sm">
            No features configured
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductPreview;

